export interface MenuPriceFields {
  price?: string | null;
  priceAmount?: number | null;
  priceCurrency?: string | null;
}

// Currencies whose readers group thousands with a dot ("171.000"). Same list as the
// backend's MenuPriceFormatter and the React admin, so every surface prints one string.
const DOT_GROUPED = new Set(['ARS', 'CLP', 'COP', 'UYU', 'PYG', 'BRL', 'BOB', 'CRC']);

// ISO code prefix (never a symbol), grouping by currency, decimals only for a fraction.
export function formatMenuPrice(amount: number, currency: string | null | undefined): string {
  // The payload is cast, not validated: a blank or non-string code is "no code",
  // never a TypeError inside render.
  const trimmed = typeof currency === 'string' ? currency.trim() : '';
  const code = trimmed === '' ? null : trimmed.toUpperCase();
  // de-DE, not es-AR: identical output for every case (171.000, 12.990, 171.000,50) but
  // CLDR minimumGroupingDigits 1, so four-digit amounts group on every engine. es-AR
  // inherits 2 from es and would print "1500" wherever `useGrouping: true` keeps its
  // pre-Intl-v3 "locale default" meaning (Safari < 15.4), diverging from the backend's
  // number_format, which always groups. en-US is already 1.
  const locale = code === null || DOT_GROUPED.has(code) ? 'de-DE' : 'en-US';
  const decimals = Number.isInteger(amount) ? 0 : 2;
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
  }).format(amount);
  return code ? `${code} ${formatted}` : formatted;
}

// Numbers first, the stored string second, null when the menu has no price. A
// non-positive amount is not a price (the backend formatter prints nothing for it).
// The string fallback also covers a numeric backend that could not parse a hand-typed
// legacy price: the diner keeps seeing what the restaurant wrote until it is fixed in
// the admin.
export function menuPriceLabel(menu: MenuPriceFields): string | null {
  if (typeof menu.priceAmount === 'number' && menu.priceAmount > 0) {
    return formatMenuPrice(menu.priceAmount, menu.priceCurrency);
  }
  const text = menu.price?.trim();
  return text ? text : null;
}

export function menuHasPrice(menu: MenuPriceFields): boolean {
  return menuPriceLabel(menu) !== null;
}
